import questCanonicalSlugsJson from "~/lib/quest-canonical-slugs.json";
import questSlugMapJson from "~/lib/quest-slug-map.json";
import questLocalizedSlugsJson from "~/lib/quest-localized-slugs.json";
import { routing } from "~/i18n/routing";
import { getCanonicalUrl } from "~/lib/routing";

const canonicalSlugMap = questCanonicalSlugsJson as Record<string, string>;
const questSlugMap = questSlugMapJson as Record<string, string>;
const localizedSlugsMap = questLocalizedSlugsJson as Record<
  string,
  Record<string, string>
>;

/**
 * Utility functions for quest URL slug normalization, validation, and localized routing.
 */

/**
 * Checks whether a locale is an East Asian CJK script locale (Japanese, Korean, Chinese).
 * These languages use logographic or syllabic scripts that do not map to Romanized ASCII slugs.
 */
export const isCJKLocale = (locale?: string | null): boolean => {
  if (!locale) return false;
  const l = locale.toLowerCase().trim();
  return (
    l.startsWith("ja") ||
    l.startsWith("ko") ||
    l.startsWith("zh")
  );
};

/**
 * Validates that a slug is a valid URL path token.
 * A valid quest slug MUST:
 * 1. Consist only of lowercase alphanumeric characters and hyphens [a-z0-9-].
 * 2. Contain at least one ASCII letter [a-z] to prevent pure numeric strings (e.g. "1", "3", "42").
 */
export const isValidSlug = (s?: string | null): s is string => {
  if (!s || typeof s !== "string") return false;
  const trimmed = s.trim();
  if (trimmed.length === 0) return false;
  if (!/^[a-z0-9-]+$/.test(trimmed)) return false;
  return /[a-z]/.test(trimmed);
};

/**
 * Normalizes human-readable text into a clean URL-friendly slug.
 */
export const slugifyQuestText = (text: string): string => {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
};

/**
 * Resolves any quest slug (canonical or localized in any language) to its canonical English slug.
 * Example: 'fais-evoluer-un-pokemon' -> 'evolve-a-pokemon'
 *          'haz-evolucionar-1-pokemon' -> 'evolve-a-pokemon'
 *          'evolve-a-pokemon' -> 'evolve-a-pokemon'
 */
export const resolveCanonicalQuestSlug = (slug: string): string => {
  const clean = slug.toLowerCase().trim();
  if (questSlugMap[clean]) {
    return questSlugMap[clean];
  }

  // Also check if slug is formatted like a raw title with target (e.g. quest-flamigo-catch-plural-10)
  const lastHyphenIndex = clean.lastIndexOf("-");
  if (lastHyphenIndex > 0) {
    const possibleTarget = parseInt(clean.slice(lastHyphenIndex + 1), 10);
    if (!isNaN(possibleTarget)) {
      const rawTitle = clean.slice(0, lastHyphenIndex).replace(/-/g, "_");
      const keyWithTarget = `${rawTitle}:${possibleTarget}`;
      if (canonicalSlugMap[keyWithTarget]) {
        return canonicalSlugMap[keyWithTarget];
      }
    }
  }

  // Check title only in canonical English map
  const rawTitle = clean.replace(/-/g, "_");
  if (canonicalSlugMap[rawTitle]) {
    return canonicalSlugMap[rawTitle];
  }

  return clean;
};

/**
 * Returns the localized URL slug for a given quest in a target locale.
 * - For CJK locales (ko, ja, zh-tw), ALWAYS returns the canonical English slug.
 * - For Latin locales (es, fr, de, it, pt-br), returns the localized slug (e.g. 'fais-evoluer-un-pokemon').
 * - For English, returns the canonical English slug.
 */
export const getLocalizedQuestSlug = (
  slugOrCanonical: string,
  targetLocale: string,
): string => {
  const canonical = resolveCanonicalQuestSlug(slugOrCanonical);
  const loc = targetLocale.toLowerCase().trim();

  // For CJK locales (ja, ko, zh-tw), ALWAYS use the canonical English slug
  if (isCJKLocale(loc)) {
    return canonical;
  }

  const localizedSlug = localizedSlugsMap[canonical]?.[loc];
  if (isValidSlug(localizedSlug)) {
    return localizedSlug;
  }

  return canonical;
};

/**
 * Generates hreflang alternate URLs for all supported locales + x-default for a quest.
 */
export const getQuestHreflangAlternates = (
  slugOrCanonical: string,
): Record<string, string> => {
  const canonical = resolveCanonicalQuestSlug(slugOrCanonical);
  const languages: Record<string, string> = {};

  for (const loc of routing.locales as readonly string[]) {
    const locSlug = getLocalizedQuestSlug(canonical, loc);
    languages[loc] = getCanonicalUrl(loc, `/quests/${locSlug}`);
  }

  const defaultSlug = getLocalizedQuestSlug(canonical, routing.defaultLocale);
  languages["x-default"] = getCanonicalUrl(
    routing.defaultLocale,
    `/quests/${defaultSlug}`,
  );

  return languages;
};

/**
 * Generates full Next.js metadata alternates object (canonical + hreflang languages) for a quest page.
 */
export const getQuestPageAlternates = (
  locale: string,
  slugOrCanonical: string,
) => {
  const canonical = resolveCanonicalQuestSlug(slugOrCanonical);
  const currentLocSlug = getLocalizedQuestSlug(canonical, locale);
  return {
    canonical: getCanonicalUrl(locale, `/quests/${currentLocSlug}`),
    languages: getQuestHreflangAlternates(canonical),
  };
};

/**
 * Resolves the canonical English task slug from the quest title identifier and target count.
 * Priority:
 * 1. Exact match in canonical English slug map (`title:target` e.g. `challenge_buddy_affection_plural:3` -> `earn-3-hearts-with-your-buddy`)
 * 2. General match in canonical English slug map (`title` e.g. `quest_evolve_singular` -> `evolve-a-pokemon`)
 * 3. Fallback normalized English title tokens
 */
export const getFallbackTaskSlug = (
  title?: string | null,
  target?: number | null,
): string | null => {
  if (!title) return null;

  // 1. Check title + target in canonical English map
  if (target) {
    const keyWithTarget = `${title}:${target}`;
    const slugWithTarget = canonicalSlugMap[keyWithTarget];
    if (slugWithTarget) {
      return slugWithTarget;
    }
  }

  // 2. Check title only in canonical English map
  const slugFromTitle = canonicalSlugMap[title];
  if (slugFromTitle) {
    return slugFromTitle;
  }

  // 3. Fallback: slugify clean English-style title tokens
  const cleanTitle = title
    .replace(/^quest_|^challenge_/, "")
    .replace(/_singular|_plural|_single/g, "")
    .replace(/_/g, " ");

  const targetSuffix = target && target > 1 ? ` ${target}` : "";
  const slug = slugifyQuestText(`${cleanTitle}${targetSuffix}`);
  return isValidSlug(slug) ? slug : null;
};

interface ResolveQuestSlugParams {
  taskSlug?: string | null;
  translatedTitle?: string | null;
  title?: string | null;
  target?: number | null;
  locale?: string | null;
}

/**
 * Resolves the optimal URL slug for a quest link:
 * - For CJK locales (ko, ja, zh-tw), ALWAYS use canonical English slugs (e.g. 'evolve-a-pokemon').
 *   Never attempt to ASCII-slugify CJK characters, which strips text and leaves lone digits like '1'.
 * - For Latin locales (es, fr, de, it, pt-br), prefer valid localized slugs (e.g. 'haz-evolucionar-1-pokemon')
 *   with fallback to the canonical English slug.
 * - If taskSlug is missing or cached without it, ALWAYS fall back to the canonical English slug (e.g. 'evolve-a-pokemon').
 */
export const resolveQuestUrlSlug = ({
  taskSlug,
  translatedTitle,
  title,
  target,
  locale,
}: ResolveQuestSlugParams): string | null => {
  const loc = locale ?? routing.defaultLocale;
  const isCJK = isCJKLocale(loc);

  // 1. If we have a valid taskSlug, return the authoritative localized slug
  if (isValidSlug(taskSlug)) {
    return getLocalizedQuestSlug(taskSlug, loc);
  }

  // 2. If taskSlug is not directly provided, get canonical English slug from title & target
  const fallbackEnglishSlug = title ? getFallbackTaskSlug(title, target) : null;
  if (fallbackEnglishSlug && isValidSlug(fallbackEnglishSlug)) {
    return getLocalizedQuestSlug(fallbackEnglishSlug, loc);
  }

  // 3. For CJK locales (ko, ja, zh-tw), ALWAYS use the canonical English slug
  if (isCJK) {
    return fallbackEnglishSlug;
  }

  // 4. For Latin locales, try localized title if available, normalized to target locale
  if (translatedTitle) {
    const localSlug = slugifyQuestText(translatedTitle);
    if (isValidSlug(localSlug)) {
      const canonical = resolveCanonicalQuestSlug(localSlug);
      return getLocalizedQuestSlug(canonical, loc);
    }
  }

  // Fall back to canonical English slug for Latin locales as well
  return fallbackEnglishSlug;
};
